import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Table, Button, Form, Row, Col, Container, Dropdown, ButtonGroup } from "react-bootstrap";
import { DatatableWrapper, Pagination, PaginationOptions, TableBody, TableHeader } from "react-bs-datatable";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisV, faSave, faPlus } from "@fortawesome/free-solid-svg-icons";
import { faStar as solidStar } from "@fortawesome/free-solid-svg-icons";
import { faStar as regularStar } from "@fortawesome/free-regular-svg-icons";
import Spinner from "../../components/spinner/Spinner";
import { useFeatureAccess } from "../../hooks/useFeatureAccess";
import { addPageFavorite, createPageView, fetchPageFavorite, removePageFavorite } from "../../store/actions/users/users.actions";
import { useNavigate } from "react-router-dom";
import { enrichContacts, getEnrichContacts } from "../../store/actions/enrich/enrich.action";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import "./index.css";
import MultiSelectDropdown from "../../components/MultiSelectDropdown";
import PricingModal from "../../components/pricingModal";
import EnrichmentConfirmationModal from "../../components/EnrichmentConfirmationModal";
import { FaInfoCircle } from "react-icons/fa";
import LoginModal from "../../components/LoginModal";

const ContactEnrichment = () => {
  const path = window.location.pathname.slice(1);
  const { user } = useSelector((state) => state.auth);
  const { user: profile } = useSelector((state) => state.userDetails);
  const { contacts: contactsList, organizations, loading, enrichLoading } = useSelector((state) => state.enrichActions);
  const { hasFeatureAccess, enforceAccess } = useFeatureAccess("Contact Enrichment");
  const { isFavorite, loading: fetchLoader } = useSelector((state) => state.fetchPageFavorite);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [rowsPerPage, setRowsPerPage] = useState(parseInt(localStorage.getItem("rowsPerPage")) || 20);
  const [currentPage, setCurrentPage] = useState(1);
  const [maxPage, setMaxPage] = useState(1);
  const [contacts, setContacts] = useState([]);
  const [loginModalShow, setLoginModalShow] = useState(false);
  const [pricingModalShow, setPricingModalShow] = useState(false);
  const [enrichmentModalShow, setEnrichmentModalShow] = useState(false);
  const [states, setStates] = useState(JSON.parse(localStorage.getItem("enrich_states") || "[]"));
  const [filters, setFilters] = useState({
    title: "",
    organizations: [],
    companySizes: []
  });
  const [sortState, setSortState] = useState(() => {
    const storedSort = localStorage.getItem("enrichTableSort");
    return storedSort
      ? JSON.parse(storedSort)
      : { prop: "created_date", order: "desc" };
  });

  // Pagination for organization dropdown
  const [orgPage, setOrgPage] = useState(0);
  const [orgSearch, setOrgSearch] = useState("");
  const [orgOptions, setOrgOptions] = useState([]);
  const [hasMoreOrgs, setHasMoreOrgs] = useState(true);
  const [isLoadingOrgs, setIsLoadingOrgs] = useState(false);
  const PAGE_SIZE = 50;

  // Company size filter options
  const companySizeOptions = [
    { value: "1-10", label: "1-10 (Micro)" },
    { value: "11-50", label: "11-50 (Small)" },
    { value: "51-200", label: "51-200 (Medium)" },
    { value: "201-500", label: "201-500 (Large)" },
    { value: "500+", label: "500+ (Enterprise)" }
  ];

  const creditsRequired = contactsList?.count || 0;
  const totalCost = creditsRequired * 2; // 2 credits per contact

  const fetchTableData = () => {
    let queryParams = `page=${currentPage}&page_size=${rowsPerPage}&sort=${sortState.order === "asc" ? sortState.prop : "-" + sortState.prop}`;
    
    // Add filters to query
    if (states?.length > 0) {
      const stateValues = states.map(state => encodeURIComponent(state.name)).join(',');
      queryParams += `&states=${stateValues}`;
    }
    
    if (filters.title) {
      queryParams += `&title=${encodeURIComponent(filters.title)}`;
    }
    
    if (filters.organizations.length > 0) {
      const orgValues = filters.organizations.map(org => encodeURIComponent(org.value)).join(',,,');
      queryParams += `&organizations=${orgValues}`;
    }

    if (filters.companySizes.length > 0) {
      const companySizeValues = filters.companySizes.map(size => encodeURIComponent(size.value)).join(',');
      queryParams += `&company_sizes=${companySizeValues}`;
    }

    dispatch(getEnrichContacts(queryParams));
  }
  
  useEffect(() => {
    setContacts(contactsList?.results || []);
    setMaxPage(Math.ceil(contactsList.count / rowsPerPage));
  }, [contactsList, user]);

  // Fetch Contacts from API
  useEffect(() => {
    fetchTableData();
  }, [dispatch, currentPage, rowsPerPage, sortState, filters, states]);

  useEffect(() => {
    const pageRecord = "Contact Enrichment";
    const type = "Contact Enrichment";

    dispatch(createPageView(pageRecord, path, type));
  }, [dispatch, path]);

  useEffect(() => {
    dispatch(fetchPageFavorite("Contact Enrichment"));
  }, [dispatch]);

  useEffect(() => {
    localStorage.setItem("enrich_states", JSON.stringify(states));
  }, [states]);

  // Initialize organization options
  useEffect(() => {
    if (Array.isArray(organizations) && organizations.length > 0) {
      loadMoreOrganizations();
    }
  }, [organizations]);

  // Load more organizations when scrolling
  const loadMoreOrganizations = () => {
    if (isLoadingOrgs || !hasMoreOrgs) return;

    setIsLoadingOrgs(true);
    setTimeout(() => {
      const orgArray = Array.isArray(organizations) ? organizations : [];
      let filteredOrgs = orgArray;
      
      // Filter by search term if provided
      if (orgSearch) {
        filteredOrgs = orgArray.filter(
          org => org && typeof org === 'string' && org.toLowerCase().includes(orgSearch.toLowerCase())
        );
      }
      
      // Get the next page of results
      const start = orgPage * PAGE_SIZE;
      const end = start + PAGE_SIZE;
      const newOptions = filteredOrgs
        .slice(start, end)
        .filter(org => org && typeof org === 'string')
        .map(org => ({
          value: org,
          label: org
        }));
      
      // Update state
      setOrgOptions(prev => [...prev, ...newOptions]);
      setOrgPage(orgPage + 1);
      setHasMoreOrgs(newOptions.length === PAGE_SIZE);
      setIsLoadingOrgs(false);
    }, 300);
  };

  // Handle search input change
  const handleOrgSearchChange = (input) => {
    setOrgSearch(input);
    setOrgPage(0);
    setOrgOptions([]);
    setHasMoreOrgs(true);
    
    // Load the first page with the new search term
    setTimeout(() => {
      const orgArray = Array.isArray(organizations) ? organizations : [];
      let filteredOrgs = orgArray;
      
      if (input) {
        filteredOrgs = orgArray.filter(
          org => org && typeof org === 'string' && org.toLowerCase().includes(input.toLowerCase())
        );
      }
      
      const newOptions = filteredOrgs
        .slice(0, PAGE_SIZE)
        .filter(org => org && typeof org === 'string')
        .map(org => ({
          value: org,
          label: org
        }));
      
      setOrgOptions(newOptions);
      setOrgPage(1);
      setHasMoreOrgs(newOptions.length === PAGE_SIZE);
    }, 300);
    
    return input;
  };

  const onSortChange = useCallback(
    (nextSort) => {
      setSortState(nextSort);
      localStorage.setItem("enrichTableSort", JSON.stringify(nextSort));
      setCurrentPage(1);
      fetchTableData();
    },
    []
  );

  const handleFavoriteToggle = () => {
    if (isFavorite) {
      dispatch(removePageFavorite({ page_name: "Contact Enrichment" }));
    } else {
      dispatch(
        addPageFavorite({ page_name: "Contact Enrichment", page_url: path })
      );
    }
  };

  const handleEnrichContacts = () => {
    if (contactsList?.count > 0) {
      if (user)
        setEnrichmentModalShow(true);
      else
        setLoginModalShow(true);
    }
  };

  const hasActiveSubscription = profile?.subscriptions?.some(
    (sub) => sub?.status === "active"
  );
  const hasEnoughCredits = (profile?.deals_wallet_credits || 0) >= totalCost;

  const confirmEnrichment = () => {
    if (hasActiveSubscription || hasEnoughCredits) {
      // Create filters object to send to backend instead of specific contact IDs
      const enrichmentFilters = {};
      
      if (states?.length > 0) {
        enrichmentFilters.states = states.map(state => state.name);
      }
      
      if (filters.title) {
        enrichmentFilters.title = filters.title;
      }
      
      if (filters.organizations.length > 0) {
        enrichmentFilters.organizations = filters.organizations.map(org => org.value);
      }

      if (filters.companySizes.length > 0) {
        enrichmentFilters.company_sizes = filters.companySizes.map(size => size.value);
      }

      dispatch(enrichContacts(enrichmentFilters, navigate));
      setEnrichmentModalShow(false);
    } else {
      setEnrichmentModalShow(false);
      setPricingModalShow(true);
    }
  };

  const handleBuyCredits = () => {
    setEnrichmentModalShow(false);
    setPricingModalShow(true);
  };

  const handleFilterChange = (field, value) => {
    setFilters({
      ...filters,
      [field]: value
    });
    setCurrentPage(1);
  };

  const onRowsPerPageChange = useCallback((rowsPerPage) => {
    setRowsPerPage(rowsPerPage);
    localStorage.setItem("rowsPerPage", rowsPerPage);
    setCurrentPage(1);
    fetchTableData();
  }, []);

  const onPageChange = useCallback((currentPage) => {
    setCurrentPage(currentPage);
    fetchTableData();
  }, []);

  const toLocalISOString = (string_date, created_at = false) => {
    if (!string_date) return "";
    const date = new Date(string_date);
    if (created_at) {
      return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
    }
    return date.toLocaleDateString();
  };

  const headers = [
    {
      prop: "name",
      title: "Name",
      isFilterable: true,
      isSortable: true,
      cell: (row) => (
        <div class="">
          {row.name}
        </div>
      ),
    },
    {
      prop: "email",
      title: "Email",
      isFilterable: false,
      isSortable: false,
      cell: (row) => (
        <div class="row-blurred">
          {row.email}
        </div>
      ),
    },
    {
      prop: "phone",
      title: "Phone",
      isFilterable: false,
      isSortable: false,
      cell: (row) => (
        <div class="row-blurred">
          {row.phone}
        </div>
      ),
    },
    {
      prop: "title",
      title: "Title",
      isFilterable: false,
      isSortable: false,
      cell: (row) => (
        <div>
          {row.title}
        </div>
      ),
    },
    {
      prop: "organization",
      title: "Organization",
      isFilterable: false,
      isSortable: false,
      cell: (row) => (
        <div>
          {row.organization}
        </div>
      ),
    },
    {
      prop: "region",
      title: "State",
      isFilterable: false,
      isSortable: false,
      cell: (row) => (
        <div>
          {row.region}
        </div>
      ),
    }
  ];

  return (
    <Container className="mt-5 container-crm">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <Row>
          <Col sm={user? 5 : 7}>
            <h1 className="heading-name pb-2">Enrich</h1>
          </Col>
          <Col sm={5}>
            <a href="https://learn.ganarpro.com/enrich-overview/">
              <FaInfoCircle fontSize="large" />
            </a>
          </Col>
          {user && (
            <Col sm={1}>
              <FontAwesomeIcon
                size={"2x"}
                icon={isFavorite ? solidStar : regularStar}
                style={{
                  color: isFavorite ? "gold" : "transparent",
                  stroke: isFavorite ? "" : "gold",
                  strokeWidth: isFavorite ? "0" : "20",
                  cursor: "pointer",
                }}
                onClick={handleFavoriteToggle}
                className="fw-bolder"
              />
            </Col>
          )}
        </Row>
      </div>

      <Row className="mb-4 gap-3 d-flex justify-content-center">
        <Col xl={3} lg={4}>
          <Form.Group>
            <Form.Label>Organization</Form.Label>
            <Select
              isMulti
              options={isLoadingOrgs ? [...orgOptions, { value: "Loading...", label: "Loading..." }] : orgOptions}
              value={filters.organizations || []}
              onChange={(selected) => handleFilterChange('organizations', selected || [])}
              onInputChange={handleOrgSearchChange}
              onMenuScrollToBottom={loadMoreOrganizations}
              placeholder="Select organizations"
              classNamePrefix="select"
              noOptionsMessage={() => isLoadingOrgs ? "Loading..." : "No organizations found"}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                menu: (base) => ({ ...base, zIndex: 9999 }),
              }}
            />
            {/* <Form.Text className="text-muted">
              Scroll to load more organizations
            </Form.Text> */}
          </Form.Group>
        </Col>
        <Col xl={2} lg={3}>
          <Form.Group>
            <Form.Label>Employee Count</Form.Label>
            <Select
              isMulti
              options={companySizeOptions}
              value={filters.companySizes || []}
              onChange={(selected) => handleFilterChange('companySizes', selected || [])}
              placeholder="Select count"
              classNamePrefix="select"
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                menu: (base) => ({ ...base, zIndex: 9999 })
              }}
            />
          </Form.Group>
        </Col>
        <Col xl={2} lg={3}>
          <Form.Group>
            <Form.Label>Title</Form.Label>
            <Form.Control
              type="text"
              value={filters.title}
              onChange={(e) => handleFilterChange('title', e.target.value)}
              placeholder="Filter by title"
            />
          </Form.Group>
        </Col>
        <Col xl={2} lg={3}>
          <Form.Group>
            <Form.Label>State</Form.Label>
            <MultiSelectDropdown
              customHandler={(e) => setStates(e)}
              selectedRegions={states}
            />
          </Form.Group>
        </Col>
        <Col xl={2} lg={3} className="d-flex align-items-center justify-content-end">
          <Button 
            variant="primary" 
            onClick={handleEnrichContacts} 
            disabled={contactsList?.count === 0 || enrichLoading}
            className="mt-3 w-100"
          >
            {enrichLoading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                ></span>
                <span className="visually-hidden">
                  Loading...
                </span>
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faPlus} className="me-2" /> Add All to CRM ({contactsList?.count || 0})
              </>
            )}
          </Button>
        </Col>
      </Row>

      {loading ? (
        <div className="text-center my-5">
          <Spinner />
        </div>
      ) : (
        <DatatableWrapper
          headers={headers}
          body={contacts}
          sortProps={sortState}
          onSort={onSortChange}
          paginationOptionsProps={{
            initialState: {
              rowsPerPage: 10,
              options: [10, 20, 50, 100, 200, 500],
            }
          }}
        >
          <div className="d-flex flex-row align-items-center justify-content-between mb-3 flex-wrap">
            <h2 className="fs-3">Total Contacts {contactsList?.count}</h2>
            <PaginationOptions
              controlledProps={{
                rowsPerPage,
                onRowsPerPageChange,
              }}
              style={{ width: "100%" }}
            ></PaginationOptions>
            <Pagination
              controlledProps={{
                currentPage,
                maxPage,
                onPageChange,
              }}
            />
          </div>
          <Table striped hover responsive className="enrich-table">
            <TableHeader
              className="fixed-header"
              controlledProps={{
                sortState,
                onSortChange,
              }} 
            />
            <TableBody />
          </Table>
        </DatatableWrapper>
      )}
      <PricingModal
        show={pricingModalShow}
        app="credit"
        costs={totalCost - profile?.deals_wallet_credits}
        onHide={() => setPricingModalShow(false)}
      />
      <LoginModal
        show={loginModalShow}
        onHide={() => setLoginModalShow(false)}
      />
      <EnrichmentConfirmationModal
        show={enrichmentModalShow}
        onHide={() => setEnrichmentModalShow(false)}
        contacts={contactsList}
        totalCost={totalCost}
        profile={profile}
        enrichLoading={enrichLoading}
        onConfirm={confirmEnrichment}
        onBuyCredits={handleBuyCredits}
      />
    </Container>
  );
};

export default ContactEnrichment;